// components/ReviewResponder.tsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styles from './ReviewResponder.module.scss';
import { db } from '../firebaseConfig';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

const toneOptions = ['friendly', 'professional', 'apologetic', 'enthusiastic'] as const;

// Use a base URL if you deploy API on a different origin; else keep relative
const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

const ReviewResponder: React.FC = () => {
  const [text, setText] = useState('');
  const [tone, setTone] = useState<string>('friendly');
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);


  const [autoReplyEnabled, setAutoReplyEnabled] = useState(false);
  const [rules, setRules] = useState({
    5: 'cheerful',
    4: 'friendly',
    3: 'professional',
    2: 'apologetic',
    1: 'apologetic',
  });
  const [rulesLoading, setRulesLoading] = useState(true);
  const [rulesError, setRulesError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Load showRulesForm from localStorage
  const [showRulesForm, setShowRulesForm] = useState(() => {
    const saved = localStorage.getItem('showRulesForm');
    return saved === null ? true : saved === 'true';
  });

  // Persist showRulesForm to localStorage
  useEffect(() => {
    localStorage.setItem('showRulesForm', showRulesForm.toString());
  }, [showRulesForm]);

  // Track changes to rules or enabled state
  useEffect(() => {
    setHasChanges(true);
    setSaveSuccess(false);
  }, [autoReplyEnabled, rules]);

  // Load rules from Firestore on mount
  useEffect(() => {
    const fetchRules = async () => {
      setRulesLoading(true);
      setRulesError('');
      try {
        const user = getAuth().currentUser;
        if (!user) throw new Error('Not authenticated');
        const ref = doc(db, 'autoReplyRules', user.uid);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data();
          setAutoReplyEnabled(!!data.enabled);
          setRules(data.rules || rules);
          setHasChanges(false);
        }
      } catch (err) {
        setRulesError('Failed to load auto-reply rules.');
      }
      setRulesLoading(false);
    };
    fetchRules();
    // eslint-disable-next-line
  }, []);

  // Save rules to Firestore
  const saveRules = async () => {
    try {
      const user = getAuth().currentUser;
      if (!user) throw new Error('Not authenticated');
      const ref = doc(db, 'autoReplyRules', user.uid);
      await setDoc(ref, { enabled: autoReplyEnabled, rules });
      setSaveSuccess(true);
      setHasChanges(false);
      setShowRulesForm(false); // Hide after save
    } catch (err) {
      setRulesError('Failed to save rules.');
    }
  };

  const generateResponse = async () => {
    setLoading(true);
    setResponse('');
    try {
      const token = await getAuth().currentUser?.getIdToken();
      const res = await axios.post(
        `${API_BASE}/api/ai/reply`,
        {
          reviewText: text,   // was "text" previously
          tone,
          // rating: optional here (you can add a selector if you want)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResponse(res.data.response);
    } catch (err) {
      console.error(err);
      alert('Failed to generate response.');
    }
    setLoading(false);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(response);
  };

  const saveResponse = async () => {
    const token = await getAuth().currentUser?.getIdToken();
    try {
      await axios.post(
        '/api/ai/save-response',
        { text, response, tone },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Saved!');
    } catch (err) {
      console.error(err);
      alert('Failed to save response.');
    }
  };

  return (
    <div className={styles.reviewResponder}>
      <h2>AI Review Responder</h2>

      {/* Auto-Reply Mode Section */}
      <div className={styles.autoReplySection}>
        <h3>Auto-Reply Mode</h3>
        <p>
          When enabled, ReviewBoost AI will automatically generate and save replies to new reviews based on your rules below.
        </p>
        <label>
          <input
            type="checkbox"
            checked={autoReplyEnabled}
            onChange={e => setAutoReplyEnabled(e.target.checked)}
            disabled={rulesLoading}
          />
          Enable Auto-Reply Mode
        </label>

        {rulesLoading && <div>Loading rules...</div>}
        {rulesError && <div style={{ color: 'red' }}>{rulesError}</div>}

        <div className={styles.rulesConfig}>
          <h4>Auto-Reply Rules (by rating):</h4>

          {showRulesForm ? (
            <>
              {[5, 4, 3, 2, 1].map(rating => (
                <div key={rating} className={styles.ruleRow}>
                  <span>{rating}★</span>
                  <select
                    value={rules[rating as keyof typeof rules]}
                    onChange={e =>
                      setRules(prev => ({
                        ...prev,
                        [rating]: e.target.value
                      }))
                    }
                    disabled={rulesLoading}
                  >
                    <option value="cheerful">cheerful</option>
                    <option value="friendly">friendly</option>
                    <option value="professional">professional</option>
                    <option value="apologetic">apologetic</option>
                    <option value="enthusiastic">enthusiastic</option>
                  </select>
                </div>
              ))}
              <button
                onClick={saveRules}
                disabled={rulesLoading || !hasChanges}
              >
                Save Rules
              </button>
            </>
          ) : (
            <button onClick={() => setShowRulesForm(true)}>
              Edit Auto-Reply Rules
            </button>
          )}

          {saveSuccess && <div style={{ color: 'green', margin: '10px' }}>Rules saved!</div>}
        </div>
      </div>

      {/* Divider */}
      <hr style={{ margin: '2rem 0' }} />

      {/* Manual Reply Generator Section */}
      <div className={styles.manualReplySection}>
        <h3>Manual Reply Generator</h3>

        <textarea
          rows={6}
          value={text}
          placeholder="Paste a review here..."
          onChange={(e) => setText(e.target.value)}
        />
        <select value={tone} onChange={(e) => setTone(e.target.value)}>
          {toneOptions.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
        <button onClick={generateResponse} disabled={loading}>
          {loading ? 'Generating...' : 'Generate Response'}
        </button>

        {response && (
          <div className={styles.responseOutput}>
            <h4>Generated Response:</h4>
            <pre>{response}</pre>
            <button onClick={copyToClipboard}>Copy</button>
            <button onClick={saveResponse}>💾 Save</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewResponder;
